import CrudSection from "../components/CrudSection";
import FeatureDashboard from "../../../components/base/FeatureDashboard";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import type { CrudColumn, CrudField } from "../types";

// ===========================================================================
// Pagos y comercio — bespoke sections.
// ===========================================================================

export function PagosDashboard() {
  return (
    <FeatureDashboard noticeKey="nuvexAccess.mockNotice"
      title="Pagos y comercio"
      subtitle="Cuotas, pagos, punto de venta e ingresos"
      icon="ri-bank-card-line"
      stats={[
        { label: "Ingresos del mes", value: "$842,300", icon: "ri-money-dollar-circle-line", change: "+9.2%", positive: true },
        { label: "Cuotas pagadas", value: "88%", icon: "ri-check-double-line", change: "+3%", positive: true },
        { label: "Adeudos", value: "$96,400", icon: "ri-error-warning-line", change: "-4%", positive: true },
        { label: "Ventas POS hoy", value: "$12,180", icon: "ri-store-2-line" },
      ]}
      chart={{
        title: "Ingresos por mes",
        data: [
          { name: "Ene", value: 720 }, { name: "Feb", value: 680 }, { name: "Mar", value: 790 },
          { name: "Abr", value: 810 }, { name: "May", value: 770 }, { name: "Jun", value: 842 },
        ],
      }}
      recent={{
        title: "Pagos recientes",
        items: [
          { primary: "Cuota mantenimiento — Depto 402", secondary: "$1,800 · Hoy 10:30", badge: "Pagado", tone: "ok" },
          { primary: "Venta POS — Cafetería", secondary: "$240 · Hoy 10:12", badge: "POS", tone: "info" },
          { primary: "Cuota mantenimiento — Casa 7", secondary: "$1,800 · Vencida", badge: "Adeudo", tone: "danger" },
        ],
      }}
    />
  );
}

// --- Cuotas y cobros -------------------------------------------------------
interface Cuota extends Record<string, unknown> {
  id: string; concepto: string; unidad: string; monto: string; vencimiento: string; estado: string;
}
const cuotaColumns: CrudColumn<Cuota>[] = [
  { key: "concepto", label: "Concepto" },
  { key: "unidad", label: "Unidad" },
  { key: "monto", label: "Monto", align: "right" },
  { key: "vencimiento", label: "Vencimiento" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const cuotaFields: CrudField[] = [
  { key: "concepto", label: "Concepto", required: true, placeholder: "Cuota de mantenimiento" },
  { key: "unidad", label: "Unidad", required: true, placeholder: "Depto 402" },
  { key: "monto", label: "Monto", required: true, placeholder: "$1,800" },
  { key: "vencimiento", label: "Vencimiento", type: "date", required: true },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Pendiente", options: [
    { value: "Pendiente", label: "Pendiente" }, { value: "Pagado", label: "Pagado" }, { value: "Vencido", label: "Vencido" },
  ] },
];
const cuotaSeed: Cuota[] = [
  { id: "q1", concepto: "Cuota mantenimiento", unidad: "Depto 402", monto: "$1,800", vencimiento: "2026-06-05", estado: "Pagado" },
  { id: "q2", concepto: "Cuota mantenimiento", unidad: "Casa 7", monto: "$1,800", vencimiento: "2026-06-05", estado: "Vencido" },
  { id: "q3", concepto: "Fondo de reserva", unidad: "Depto 510", monto: "$600", vencimiento: "2026-06-20", estado: "Pendiente" },
];
export const CuotasSection = () => (
  <CrudSection<Cuota> title="Cuotas y cobros" subtitle="Cuotas de mantenimiento y cobros"
    icon="ri-coins-line" columns={cuotaColumns} fields={cuotaFields} seed={cuotaSeed} searchKey="unidad" />
);

// --- Pagos (log) -----------------------------------------------------------
interface Pago extends Record<string, unknown> {
  id: string; fecha: string; concepto: string; unidad: string; monto: string; metodo: string; estado: string;
}
const pagoColumns: CrudColumn<Pago>[] = [
  { key: "fecha", label: "Fecha" },
  { key: "concepto", label: "Concepto" },
  { key: "unidad", label: "Unidad" },
  { key: "monto", label: "Monto", align: "right" },
  { key: "metodo", label: "Método" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const pagoSeed: Pago[] = [
  { id: "pg1", fecha: "2026-06-08 10:30", concepto: "Cuota mantenimiento", unidad: "Depto 402", monto: "$1,800", metodo: "Tarjeta", estado: "Pagado" },
  { id: "pg2", fecha: "2026-06-08 09:50", concepto: "Reserva salón", unidad: "Casa 12", monto: "$900", metodo: "Transferencia", estado: "Pagado" },
  { id: "pg3", fecha: "2026-06-07 18:22", concepto: "Estacionamiento", unidad: "Depto 510", monto: "$450", metodo: "Efectivo", estado: "Pagado" },
];
export const PagosSection = () => (
  <CrudSection<Pago> title="Pagos" subtitle="Historial de pagos recibidos"
    icon="ri-bank-card-line" columns={pagoColumns} fields={[]} seed={pagoSeed} searchKey="unidad" readOnly />
);

// --- Productos / POS -------------------------------------------------------
interface Producto extends Record<string, unknown> {
  id: string; producto: string; categoria: string; precio: string; stock: string; estado: string;
}
const productoColumns: CrudColumn<Producto>[] = [
  { key: "producto", label: "Producto" },
  { key: "categoria", label: "Categoría" },
  { key: "precio", label: "Precio", align: "right" },
  { key: "stock", label: "Stock", align: "right" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const productoFields: CrudField[] = [
  { key: "producto", label: "Producto", required: true, placeholder: "Café americano" },
  { key: "categoria", label: "Categoría", type: "select", defaultValue: "Cafetería", options: [
    { value: "Cafetería", label: "Cafetería" }, { value: "Tienda", label: "Tienda" }, { value: "Servicios", label: "Servicios" },
  ] },
  { key: "precio", label: "Precio", required: true, placeholder: "$45" },
  { key: "stock", label: "Stock", type: "number", defaultValue: 0 },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Inactivo", label: "Inactivo" },
  ] },
];
const productoSeed: Producto[] = [
  { id: "pr1", producto: "Café americano", categoria: "Cafetería", precio: "$45", stock: "120", estado: "Activo" },
  { id: "pr2", producto: "Acceso gimnasio (día)", categoria: "Servicios", precio: "$80", stock: "—", estado: "Activo" },
  { id: "pr3", producto: "Toalla club", categoria: "Tienda", precio: "$150", stock: "8", estado: "Inactivo" },
];
export const ProductosSection = () => (
  <CrudSection<Producto> title="Productos / POS" subtitle="Catálogo del punto de venta"
    icon="ri-store-2-line" columns={productoColumns} fields={productoFields} seed={productoSeed} searchKey="producto" />
);

// --- Reportes de ingresos (dashboard) --------------------------------------
export function ReportesIngresosDashboard() {
  return (
    <FeatureDashboard noticeKey="nuvexAccess.mockNotice"
      title="Reportes de ingresos"
      subtitle="Ingresos, adeudos y conciliación"
      icon="ri-line-chart-line"
      stats={[
        { label: "Ingresos del mes", value: "$842,300", icon: "ri-money-dollar-circle-line", change: "+9.2%", positive: true },
        { label: "Cuotas", value: "$612,000", icon: "ri-coins-line" },
        { label: "POS", value: "$184,300", icon: "ri-store-2-line" },
        { label: "Reservas", value: "$46,000", icon: "ri-calendar-check-line" },
      ]}
      chart={{
        title: "Ingresos por fuente (mes)",
        data: [
          { name: "Cuotas", value: 612 }, { name: "POS", value: 184 },
          { name: "Reservas", value: 46 }, { name: "Otros", value: 0 },
        ],
      }}
      recent={{
        title: "Conciliación reciente",
        items: [
          { primary: "Conciliación bancaria", secondary: "Junio · cuadrada", badge: "OK", tone: "ok" },
          { primary: "Adeudos por cobrar", secondary: "$96,400 · 12 unidades", badge: "Adeudo", tone: "warn" },
        ],
      }}
    />
  );
}
